<template>
  <div class="card mb-4 shadow-sm">
    <div class="card-header bg-white">
      <h5 class="mb-0"><i class="fa fa-percent me-2"></i>GST/HST Registration</h5>
    </div>
    <div class="card-body">
      <!-- Asher files nothing on the driver's behalf and holds nothing up: the driver registers with
           the CRA themselves and comes back with the number, so this card only collects and saves it.
           There is deliberately no request-status state to read — the "register one for me"
           authorization form #252 shipped is hidden for launch, and its endpoint, admin queue and
           resolve/reject flow are untouched behind it. -->
      <p class="text-muted mb-3">
        You charge tax on every ride and file it yourself, so you need your own GST/HST number before
        you can go online. <strong>If you have driven for another rideshare or delivery company before,
        you almost certainly already have one</strong> — it stays with you, so enter that number here
        rather than registering again. Otherwise the CRA registers you free of charge; the guide below
        walks you through it, and you come back here once you have your number.
      </p>

      <label class="form-label f-w-600" for="driver-status-gst-hst-number-input">GST/HST Number</label>
      <input id="driver-status-gst-hst-number-input" class="form-control"
             :class="{ 'is-invalid': gstHstNumber.length > 0 && !isGstHstNumberValid }"
             type="text" v-model="gstHstNumber" placeholder="e.g., 123456789RT0001">
      <div class="invalid-feedback" v-if="gstHstNumber.length > 0 && !isGstHstNumberValid">
        Enter a valid GST/HST number: nine digits, optionally followed by RT and four digits.
      </div>
      <button class="btn btn-primary mt-3" @click="saveGstHstNumber"
              :disabled="savingNumber || !isGstHstNumberValid">
        <span v-if="savingNumber" class="spinner-border spinner-border-sm me-1"></span>
        {{ savingNumber ? 'Saving...' : 'Save Number' }}
      </button>
      <div v-if="saveNumberError" class="alert alert-danger mt-3 mb-0">{{ saveNumberError }}</div>

      <div class="border rounded p-3 mt-4">
        <h6 class="mb-2"><i class="fa fa-info-circle me-2"></i>How to get your GST/HST number</h6>
        <p class="text-muted mb-2">
          The CRA registers rideshare drivers directly, and their guide covers every step:
        </p>
        <p class="mb-3">
          <a href="https://www.canada.ca/en/revenue-agency/services/tax/businesses/topics/business-registration/business-number-program-account/register-rideshare-taxi.html"
             target="_blank" rel="noopener noreferrer">
            Register as a rideshare driver or taxi operator — Canada.ca<i class="fa fa-external-link ms-1"></i>
          </a>
        </p>
        <ul class="text-muted mb-3 cra-guide-list">
          <li>
            As a rideshare driver you are a sole proprietor, so you need both a Business Number (BN)
            and a GST/HST account — no matter how much you earn.
          </li>
          <li>
            A sole proprietor can only hold one Business Number, so if you registered while driving for
            someone else, that same number is the one to enter above.
          </li>
          <li>
            You register online through Business Registration Online, which needs a CRA account. You
            cannot open one without having filed a T1 personal return in the last two years.
          </li>
          <li>
            If you cannot get a CRA account, you can register by mail instead, using Form RC1.
          </li>
        </ul>
        <p class="text-muted mb-0">
          Stuck, or not sure which of these applies to you? Email
          <a href="mailto:support@asher-rides.com">support@asher-rides.com</a> and we'll help you
          through it.
        </p>
      </div>
    </div>
  </div>
</template>

<script setup>
import { ref, computed } from 'vue';

const emit = defineEmits(['saved']);

const gstHstNumber = ref('');
const savingNumber = ref(false);
const saveNumberError = ref(null);

const isGstHstNumberFormat = (val) => /^\d{9}(RT\d{4})?$/.test(String(val ?? '').replace(/[\s-]/g, '').toUpperCase());
const isGstHstNumberValid = computed(() => isGstHstNumberFormat(gstHstNumber.value));

const saveGstHstNumber = async () => {
  saveNumberError.value = null;
  savingNumber.value = true;
  try {
    const response = await fetch('/api/driver/application-status/gst-hst-number', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ gstHstNumber: gstHstNumber.value }),
    });
    if (!response.ok) {
      const text = await response.text();
      throw new Error(text || 'Unable to save your GST/HST number.');
    }
    emit('saved');
  } catch (e) {
    saveNumberError.value = e.message || 'Unable to save your GST/HST number.';
  } finally {
    savingNumber.value = false;
  }
};
</script>

<style scoped>
/* The admin theme strips list markers from every ul, which runs these three points together as
   unbroken prose. */
.cra-guide-list {
  list-style: disc;
  padding-left: 1.25rem;
}

.cra-guide-list li + li {
  margin-top: 0.35rem;
}
</style>
